import { useTranslation } from "react-i18next";
import "./Navigation.css";
import { useEffect, useState, useRef } from "react";
import AnchorLinks from "./anchorLinks/AnchorLinks";
import NavLinks from "./navLinks/NavLinks";
import Underline from "./underLine/Underline";
import Divider from "../../../components/divider/Divider";
import { useLocation } from "react-router-dom";

const LINKS = [
  {
    name: "about",
  },
  {
    name: "philosophy",
  },
  {
    name: "events",
  },
  {
    name: "contacts",
  },
];

const NAV_LINKS = [
  {
    name: "tips",
  },
  {
    name: "projects",
  },
  {
    name: "reports",
  },
  {
    name: "team",
  },
];

export default function Navigation({ isActive, setShowNavigation }) {
  const { t } = useTranslation("translation", { keyPrefix: "navigation" });
  const [activeLink, setActiveLink] = useState(0);
  const [underlineStyle, setUnderlineStyle] = useState({});
  const linkRefs = useRef([]);
  const location = useLocation();

  useEffect(() => {
    const hideNavigation = () => setShowNavigation(false);

    document.addEventListener("click", hideNavigation);

    return () => document.removeEventListener("click", hideNavigation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (linkRefs.current[activeLink]) {
        const activeLinkRect =
          linkRefs.current[activeLink].getBoundingClientRect();
        setUnderlineStyle({
          width: activeLinkRect.width - 4,
          left: activeLinkRect.left - 9,
        });
      }
    };

    setTimeout(handleResize, 100);
    document.fonts.ready.then(handleResize);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activeLink, t]);

  useEffect(() => {
    const url = location;

    if (url.pathname === "/") {
      if (url.hash === "") {
        setActiveLink(0);
      } else {
        LINKS.forEach((e, index) => {
          if (`#${e.name}` === url.hash) {
            setActiveLink(index);
          }
        });
      }
    } else {
      const pathParts = url.pathname.split("/");
      if (pathParts.length > 1) {
        const firstValue = pathParts[1];
        NAV_LINKS.forEach((e, index) => {
          if (e.name === firstValue) {
            setActiveLink(index + LINKS.length);
          }
        });
      }
    }
  }, [location]);

  useEffect(() => {
    if (location.pathname !== "/") return undefined;

    let animationFrame;

    const updateActiveSection = () => {
      animationFrame = undefined;
      const sections = LINKS.map((link) => document.getElementById(link.name));
      const pageBottomReached =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 4;

      if (pageBottomReached && sections[sections.length - 1]) {
        setActiveLink(sections.length - 1);
        return;
      }

      const marker = window.scrollY + Math.min(190, window.innerHeight * 0.3);
      let currentSection = 0;

      sections.forEach((section, index) => {
        if (section && section.offsetTop <= marker) {
          currentSection = index;
        }
      });

      setActiveLink(currentSection);
    };

    const requestUpdate = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(updateActiveSection);
      }
    };

    updateActiveSection();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [location.pathname]);

  const handleClick = (index) => {
    setActiveLink(index);
    const activeLinkRect = linkRefs.current[index].getBoundingClientRect();
    setUnderlineStyle({
      width: activeLinkRect.width - 4,
      left: activeLinkRect.left - 9,
    });
  };

  return (
    <nav
      className={`header-navbar ${isActive ? "active" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      <ul className="header-navbar__links-list">
        <AnchorLinks
          LINKS={LINKS}
          handleClick={handleClick}
          setShowNavigation={setShowNavigation}
          linkRefs={linkRefs}
          activeLink={activeLink}
        />

        <Divider />

        <NavLinks
          NAV_LINKS={NAV_LINKS}
          LINKS={LINKS}
          handleClick={handleClick}
          setShowNavigation={setShowNavigation}
          linkRefs={linkRefs}
          isActive={isActive}
          underlineStyle={underlineStyle}
          activeLink={activeLink}
        />
        <Underline isActive={isActive} underlineStyle={underlineStyle} />
      </ul>
    </nav>
  );
}
